import type { AdReport } from "@mm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { api, fieldError } from "@/lib/api";
import { day, fromLocalInput, toLocalInput } from "@/lib/dates";
import { Icon } from "@/ui/icons";
import { AreaPicker, Badge, Button, Card, Empty, ErrorText, Field, ImageUpload, Input, Section, Textarea, Toggle } from "@/ui/kit";
import { PageHeader } from "@/ui/Shell";

interface Row { id: string; businessName: string; headline: string; status: string; startsAt: string; endsAt: string; impressions: number; uniqueUsers: number; clicks: number; redeemClicks: number }

const liveTone = (a: Row) => (a.status === "published" && Date.parse(a.endsAt) > Date.now() ? (Date.parse(a.startsAt) > Date.now() ? "butter" : "sage") : a.status === "draft" ? "butter" : "mist");
const liveLabel = (a: Row) => (a.status === "published" && Date.parse(a.endsAt) > Date.now() ? (Date.parse(a.startsAt) > Date.now() ? "scheduled" : "live") : a.status);

export function Ads() {
  const nav = useNavigate();
  const q = useQuery({ queryKey: ["admin-ads"], queryFn: () => api<Row[]>("/v1/admin/ads") });
  return (
    <>
      <PageHeader title="Ads" action={<Button onClick={() => nav("/ads/new")} className="!px-4"><Icon name="plus" className="size-5" />New</Button>} />
      <ErrorText error={q.error} />
      <div className="grid gap-2">
        {(q.data ?? []).map((a) => (
          <Card key={a.id} onClick={() => nav(`/ads/${a.id}`)}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-semibold">{a.businessName}</p>
                <p className="truncate text-sm text-muted">{a.headline}</p>
                <p className="text-xs text-muted">{day(a.startsAt)} – {day(a.endsAt)}</p>
              </div>
              <Badge tone={liveTone(a)}>{liveLabel(a)}</Badge>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-1 text-center text-xs">
              {[["Views", a.impressions], ["People", a.uniqueUsers], ["Clicks", a.clicks], ["Redeems", a.redeemClicks]].map(([l, n]) => (
                <div key={l} className="rounded-xl bg-mist py-2"><b className="block text-base">{n}</b>{l}</div>
              ))}
            </div>
          </Card>
        ))}
      </div>
      {q.isSuccess && !q.data.length ? <Empty title="No ads yet">Create one for a neighborhood partner.</Empty> : null}
    </>
  );
}

const blank = () => {
  const s = new Date();
  s.setHours(0, 0, 0, 0);
  return {
    businessName: "", headline: "", body: "", ctaLabel: "View offer", landingTitle: "", landingBody: "", promoCode: "", redeemUrl: "",
    imageId: null as string | null, imageUrl: null as string | null, startsAt: toLocalInput(s), endsAt: toLocalInput(new Date(s.getTime() + 30 * 86_400_000)),
    impressionCap: "", uniqueImpressionCap: "", perUserCap: "", weight: "1", allAreas: true, areaIds: [] as number[],
  };
};

export function AdEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-ad", id], queryFn: () => api<Record<string, unknown>>(`/v1/admin/ads/${id}`), enabled: !isNew });
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const a = q.data;

  useEffect(() => {
    if (!a) return;
    const s = (k: string) => (a[k] === null || a[k] === undefined ? "" : String(a[k]));
    setF({
      businessName: s("businessName"), headline: s("headline"), body: s("body"), ctaLabel: s("ctaLabel"), landingTitle: s("landingTitle"), landingBody: s("landingBody"),
      promoCode: s("promoCode"), redeemUrl: s("redeemUrl"), imageId: (a.imageKey as string) ?? null, imageUrl: (a.imageUrl as string) ?? null,
      startsAt: toLocalInput(a.startsAt as string), endsAt: toLocalInput(a.endsAt as string), impressionCap: s("impressionCap"), uniqueImpressionCap: s("uniqueImpressionCap"),
      perUserCap: s("perUserCap"), weight: s("weight") || "1", allAreas: Boolean(a.allAreas), areaIds: (a.areaIds as number[]) ?? [],
    });
  }, [a]);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const num = (v: string) => (v ? Number(v) : null);

  async function save(publish: boolean) {
    setBusy(publish ? "publish" : "save");
    setError(null);
    try {
      const body = {
        businessName: f.businessName, headline: f.headline, body: f.body, ctaLabel: f.ctaLabel || "View offer", landingTitle: f.landingTitle, landingBody: f.landingBody,
        promoCode: f.promoCode || null, redeemUrl: f.redeemUrl || null, imageAttachmentId: f.imageId, startsAt: fromLocalInput(f.startsAt), endsAt: fromLocalInput(f.endsAt),
        impressionCap: num(f.impressionCap), uniqueImpressionCap: num(f.uniqueImpressionCap), perUserCap: num(f.perUserCap), weight: Number(f.weight) || 1,
        allAreas: f.allAreas, areaIds: f.areaIds,
      };
      const r = isNew ? await api<{ id: string }>("/v1/admin/ads", { method: "POST", body }) : await api<{ id: string }>(`/v1/admin/ads/${id}`, { method: "PUT", body });
      if (publish) await api(`/v1/admin/ads/${r.id}/status`, { method: "POST", body: { status: "published" } });
      await qc.invalidateQueries({ queryKey: ["admin-ads"] });
      await qc.invalidateQueries({ queryKey: ["admin-ad", id] });
      if (isNew) nav(`/ads/${r.id}`, { replace: true });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  const status = a?.status as string | undefined;
  return (
    <>
      <PageHeader title={isNew ? "New ad" : f.businessName || "Ad"} back="/ads" action={!isNew ? <Button variant="secondary" onClick={() => nav(`/ads/${id}/report`)}>Report</Button> : null} />
      <Section title="Preview">
        <div className="flex items-end gap-3 rounded-2xl bg-butter p-4">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold tracking-wider uppercase text-ink-soft">Sponsored · {f.businessName || "Business"}</p>
            <p className="mt-1 font-semibold">{f.headline || "Headline"}</p>
            <p className="text-sm text-ink-soft">{f.body || "One or two lines of copy."}</p>
          </div>
          <span className="rounded-full bg-ink px-3 py-2 text-sm font-semibold text-white">{f.ctaLabel || "View offer"}</span>
        </div>
      </Section>
      <Section title="Banner">
        <Field label="Business name">{(i) => <Input id={i} value={f.businessName} onChange={(e) => set("businessName", e.target.value)} />}</Field>
        <Field label="Headline" error={fieldError(error, "headline")}>{(i) => <Input id={i} maxLength={80} value={f.headline} onChange={(e) => set("headline", e.target.value)} placeholder="Free coffee for moms" />}</Field>
        <Field label="Copy">{(i) => <Textarea id={i} rows={2} maxLength={200} value={f.body} onChange={(e) => set("body", e.target.value)} />}</Field>
        <Field label="Button label">{(i) => <Input id={i} maxLength={24} value={f.ctaLabel} onChange={(e) => set("ctaLabel", e.target.value)} />}</Field>
      </Section>
      <Section title="Offer page">
        <ImageUpload url={f.imageUrl} onUploaded={(aid, url) => setF((x) => ({ ...x, imageId: aid, imageUrl: url }))} onClear={() => setF((x) => ({ ...x, imageId: null, imageUrl: null }))} />
        <div className="h-3" />
        <Field label="Title">{(i) => <Input id={i} value={f.landingTitle} onChange={(e) => set("landingTitle", e.target.value)} placeholder="A free coffee, on us" />}</Field>
        <Field label="Details">{(i) => <Textarea id={i} value={f.landingBody} onChange={(e) => set("landingBody", e.target.value)} />}</Field>
        <Field label="Promo code" hint="Moms tap to copy it; we count each copy as a redeem.">{(i) => <Input id={i} value={f.promoCode} onChange={(e) => set("promoCode", e.target.value.toUpperCase())} placeholder="COFFEEMOM10" className="font-mono tracking-widest" />}</Field>
        <Field label="Redeem link (optional)" error={fieldError(error, "redeemUrl")}>{(i) => <Input id={i} type="url" inputMode="url" value={f.redeemUrl} onChange={(e) => set("redeemUrl", e.target.value)} placeholder="https://" />}</Field>
      </Section>
      <Section title="Schedule & limits">
        <div className="grid gap-x-3 sm:grid-cols-2">
          <Field label="Starts">{(i) => <Input id={i} type="datetime-local" value={f.startsAt} onChange={(e) => set("startsAt", e.target.value)} />}</Field>
          <Field label="Ends" hint="Retires automatically." error={fieldError(error, "endsAt")}>{(i) => <Input id={i} type="datetime-local" value={f.endsAt} onChange={(e) => set("endsAt", e.target.value)} />}</Field>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Max views">{(i) => <Input id={i} inputMode="numeric" value={f.impressionCap} onChange={(e) => set("impressionCap", e.target.value.replace(/\D/g, ""))} placeholder="∞" />}</Field>
          <Field label="Max people">{(i) => <Input id={i} inputMode="numeric" value={f.uniqueImpressionCap} onChange={(e) => set("uniqueImpressionCap", e.target.value.replace(/\D/g, ""))} placeholder="∞" />}</Field>
          <Field label="Per mom">{(i) => <Input id={i} inputMode="numeric" value={f.perUserCap} onChange={(e) => set("perUserCap", e.target.value.replace(/\D/g, ""))} placeholder="∞" />}</Field>
        </div>
        <Field label="Priority (1–10)" hint="Higher shows more often when several ads compete.">{(i) => <Input id={i} inputMode="numeric" value={f.weight} onChange={(e) => set("weight", e.target.value.replace(/\D/g, "").slice(0, 2))} />}</Field>
      </Section>
      <Section title="Who sees it">
        <Toggle label="All of Chicago" checked={f.allAreas} onChange={(v) => set("allAreas", v)} />
        {!f.allAreas ? <AreaPicker value={f.areaIds} onChange={(v) => set("areaIds", v)} /> : null}
        {fieldError(error, "areaIds") ? <p className="mt-1 text-sm text-danger">{fieldError(error, "areaIds")}</p> : null}
      </Section>
      <ErrorText error={error} />
      <div className="pb-safe sticky bottom-20 z-10 -mx-4 bg-cream/95 px-4 py-3 backdrop-blur lg:bottom-0">
        {status === "published" ? (
          <div className="grid grid-cols-2 gap-2">
            <Button variant="ghost" onClick={async () => { await api(`/v1/admin/ads/${id}/status`, { method: "POST", body: { status: "paused" } }); await qc.invalidateQueries({ queryKey: ["admin-ad", id] }); }}>Pause</Button>
            <Button loading={busy === "save"} onClick={() => void save(false)}>Save</Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Button variant="ghost" loading={busy === "save"} onClick={() => void save(false)}>Save draft</Button>
            <Button variant="rose" loading={busy === "publish"} onClick={() => void save(true)}>{status === "paused" ? "Resume" : "Publish"}</Button>
          </div>
        )}
      </div>
    </>
  );
}

export function AdReportPage() {
  const { id } = useParams();
  const q = useQuery({ queryKey: ["ad-report", id], queryFn: () => api<AdReport>(`/v1/admin/ads/${id}/report`) });
  const r = q.data;
  const max = Math.max(1, ...(r?.daily.map((d) => d.impressions) ?? [1]));
  async function downloadCsv() {
    const csv = await api<string>(`/v1/admin/ads/${id}/report.csv`, { raw: true });
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = Object.assign(document.createElement("a"), { href: url, download: `ad-report-${id}.csv` });
    link.click();
    URL.revokeObjectURL(url);
  }
  return (
    <>
      <PageHeader title="Ad report" back={`/ads/${id}`} action={<Button variant="secondary" onClick={() => void downloadCsv()}>CSV</Button>} />
      <ErrorText error={q.error} />
      {r ? (
        <>
          <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
            {[["Views", r.totals.impressions, r.caps.impressionCap], ["People reached", r.totals.uniqueUsers, r.caps.uniqueImpressionCap], ["Clicks", r.totals.clicks, null], ["Redeems", r.totals.redeemClicks, null], ["Click rate", `${r.totals.ctr}%`, null]].map(([l, n, cap]) => (
              <div key={l as string} className="rounded-2xl bg-white p-3 ring-1 ring-line">
                <p className="text-2xl font-bold">{n}</p>
                <p className="text-xs text-muted">{l}{cap ? ` · cap ${cap}` : ""}</p>
              </div>
            ))}
          </div>
          <Section title="Daily views">
            {r.daily.length ? (
              <div className="flex h-40 items-end gap-1 overflow-x-auto rounded-2xl bg-white p-3 ring-1 ring-line" role="img" aria-label="Daily views chart">
                {r.daily.map((d) => (
                  <div key={d.day} className="flex min-w-6 flex-1 flex-col items-center justify-end gap-1">
                    <div className="w-full rounded-t-md bg-ink-soft" style={{ height: `${(d.impressions / max) * 100}%` }} title={`${d.day}: ${d.impressions} views, ${d.clicks} clicks`} />
                    <span className="text-[10px] text-muted">{d.day.slice(5)}</span>
                  </div>
                ))}
              </div>
            ) : <Empty title="No views yet" />}
          </Section>
          <Section title="By area">
            <ul className="divide-y divide-line rounded-2xl bg-white ring-1 ring-line">
              {r.byArea.map((b) => <li key={b.areaName} className="flex justify-between px-4 py-3 text-sm"><span>{b.areaName}</span><span className="text-muted">{b.impressions} views · {b.clicks} clicks · {b.redeemClicks} redeems</span></li>)}
            </ul>
          </Section>
        </>
      ) : null}
    </>
  );
}
